import React, { useEffect, useState, useRef } from 'react';
import { useFlowium } from '../client/FlowiumContext';
import { Notification } from '../../../client/ts/src/types';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router';
import { CheckCircleIcon, CloseIcon, SettingsIcon, TimeCircleIcon, EyeOffIcon } from './Icons';
import { getAnchoredOverlayPosition } from './overlayPosition';
import Button from './Button';

interface NotificationPanelProps {
    isOpen: boolean;
    onClose: () => void;
    anchorRef: React.RefObject<HTMLDivElement | null>;
}

const PANEL_WIDTH = 360;

const NotificationPanel: React.FC<NotificationPanelProps> = ({ isOpen, onClose, anchorRef }) => {
    const { client } = useFlowium();
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [tab, setTab] = useState<'all' | 'unread'>('unread');
    const panelRef = useRef<HTMLDivElement>(null);
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (isOpen) fetchNotifications();
    }, [isOpen]);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                panelRef.current &&
                !panelRef.current.contains(event.target as Node) &&
                anchorRef.current &&
                !anchorRef.current.contains(event.target as Node)
            ) {
                onClose();
            }
        }
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose, anchorRef]);

    const fetchNotifications = async () => {
        try {
            setLoading(true);
            const { notifications: items } = await client.getNotifications(1, 20);
            setNotifications(items);
            setError(null);
        } catch (err) {
            setError('Failed to fetch notifications');
        } finally {
            setLoading(false);
        }
    };

    const handleMarkAsRead = async (notificationIds: string[]) => {
        try {
            await client.markNotificationsRead(notificationIds);
            setNotifications(prev =>
                prev.map(n =>
                    notificationIds.includes(n.id) ? { ...n, read: true } : n
                )
            );
        } catch (err) {
            // silently handled
        }
    };

    const handleMarkAllAsRead = async () => {
        const unreadIds = notifications.filter(n => !n.read).map(n => n.id);
        if (unreadIds.length === 0) return;
        await handleMarkAsRead(unreadIds);
    };

    const [position, setPosition] = useState({ left: 0, top: 0 });
    const [panelWidth, setPanelWidth] = useState(PANEL_WIDTH);
    const [panelHeight, setPanelHeight] = useState(520);
    useEffect(() => {
        const anchor = anchorRef.current;
        if (!isOpen || !anchor) return;

        const updatePosition = () => {
            const rect = anchor.getBoundingClientRect();
            const nextPanelWidth = Math.min(PANEL_WIDTH, Math.max(0, window.innerWidth - 32));
            const nextPanelHeight = Math.min(560, Math.max(0, window.innerHeight - 32));
            setPanelWidth(nextPanelWidth);
            setPanelHeight(nextPanelHeight);
            setPosition(getAnchoredOverlayPosition({
                anchorRect: rect,
                panelWidth: nextPanelWidth,
                panelHeight: nextPanelHeight,
            }));
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                onClose();
            }
        };

        updatePosition();
        const focusFrame = window.requestAnimationFrame(() => closeButtonRef.current?.focus());
        window.addEventListener('resize', updatePosition);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            window.cancelAnimationFrame(focusFrame);
            window.removeEventListener('resize', updatePosition);
            document.removeEventListener('keydown', handleKeyDown);
            anchor.querySelector<HTMLButtonElement>('button')?.focus();
        };
    }, [isOpen, onClose, anchorRef]);

    if (!isOpen) return null;

    const filtered = tab === 'all' ? notifications : notifications.filter(n => !n.read);

    return (
        <div
            ref={panelRef}
            role="dialog"
            aria-label="Notifications"
            className="fixed z-[1000] flex overflow-hidden rounded-2xl border border-secondary bg-surface shadow-2xl"
            style={{
                left: position.left,
                top: position.top,
                width: panelWidth,
                height: panelHeight,
            }}
        >
            <div className="flex w-full flex-col">
                <div className="border-b border-secondary px-4 py-4">
                    <div className="flex items-center justify-between gap-3">
                        <span className="text-base font-semibold text-content">Notifications</span>
                        <div className="flex shrink-0 items-center gap-1">
                            <button
                                type="button"
                                aria-label="Mark all as read"
                                title="Mark all as read"
                                onClick={handleMarkAllAsRead}
                                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-content-secondary transition-colors hover:bg-hover hover:text-success"
                            >
                                <CheckCircleIcon padding={0} size={5} />
                            </button>
                            <button
                                type="button"
                                aria-label="Notification settings"
                                title="Notification settings"
                                onClick={() => {
                                    onClose();
                                    navigate('/settings/notifications');
                                }}
                                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-content-secondary transition-colors hover:bg-hover hover:text-content"
                            >
                                <SettingsIcon padding={0} size={5} />
                            </button>
                            <button
                                ref={closeButtonRef}
                                type="button"
                                aria-label="Close notifications"
                                title="Close notifications"
                                onClick={onClose}
                                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-content-secondary transition-colors hover:bg-hover hover:text-content"
                            >
                                <CloseIcon padding={0} size={4} />
                            </button>
                        </div>
                    </div>
                    <p className="mt-1 text-sm text-content-secondary">
                        Activity, uploads, and workspace updates.
                    </p>
                </div>
                <div className="flex items-center gap-2 border-b border-secondary px-4 py-3">
                    <Button
                        variant={tab === 'all' ? 'secondary' : 'ghost'}
                        size="sm"
                        onClick={() => setTab('all')}
                        icon={<TimeCircleIcon padding={0} size={4} />}
                    >
                        All
                    </Button>
                    <Button
                        variant={tab === 'unread' ? 'secondary' : 'ghost'}
                        size="sm"
                        onClick={() => setTab('unread')}
                        icon={<EyeOffIcon padding={0} size={4} />}
                    >
                        Unread
                    </Button>
                </div>
                <div className="overflow-y-auto">
                    {loading ? (
                        <div className="flex h-32 items-center justify-center text-content-secondary">Loading...</div>
                    ) : error ? (
                        <div className="flex h-32 items-center justify-center text-error">{error}</div>
                    ) : filtered.length === 0 ? (
                        <div className="flex h-32 items-center justify-center text-content-secondary">No notifications</div>
                    ) : (
                        filtered.map(notification => (
                            <div
                                key={notification.id}
                                className={`flex items-start gap-3 border-b border-secondary px-5 py-4 last:border-b-0 ${notification.read ? 'bg-surface' : 'bg-surface-secondary/70'}`}
                            >
                                <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-full bg-accent/10 text-xs font-bold text-accent">
                                    {notification.title?.charAt(0)?.toUpperCase() || '?'}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-start gap-2">
                                        <span className="text-sm font-semibold text-content">{notification.title}</span>
                                        <span className="ml-auto whitespace-nowrap text-xs text-content-tertiary">
                                            {formatDistanceToNow(new Date(notification.timestamp), { addSuffix: true })}
                                        </span>
                                    </div>
                                    <div className="mt-1 text-sm text-content-secondary">
                                        {notification.message}
                                    </div>
                                </div>
                                {!notification.read && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="self-start whitespace-nowrap"
                                        onClick={() => handleMarkAsRead([notification.id])}
                                    >
                                        Mark read
                                    </Button>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default NotificationPanel;
